import { useQuery } from '@tanstack/react-query';
import { Car, MessageSquarePlus, Star } from 'lucide-react';
import { instructorApi } from '../api/instructor';
import { useUiStore } from '../store/ui';
import { fmtDate } from '../lib/dates';
import type { StudentLesson } from '../types';
import { Badge, Button } from './charte';
import { Sheet } from './shell/Sheet';
import { PrivateNote } from './lesson/PrivateNote';

// Historique des leçons d'un élève (fenêtre de la charte, refonte du 14/10/2026) : lecture des résumés de séance
// (ex-bilans) ; « Ajouter / Compléter le résumé » ouvre la page du résumé.

const LEVEL_LABEL: Record<string, string> = { acquis: 'Acquis', en_cours: 'En cours', a_revoir: 'À revoir' };

function LessonRow({ lesson, onBilan }: { lesson: StudentLesson; onBilan: () => void }) {
  const b = lesson.bilan;
  return (
    <li className="rounded-row border border-line p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-surface-indigo text-primary">
            <Car size={17} aria-hidden />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-heading">{fmtDate(lesson.startsAt)}</p>
            <p className="text-xs text-secondary">
              {lesson.lessonType ?? 'Leçon'} · {lesson.durationHours} h
            </p>
          </div>
        </div>
        <Button size="sm" icon={MessageSquarePlus} onClick={onBilan}>
          {b ? 'Compléter le résumé' : 'Ajouter un résumé'}
        </Button>
      </div>
      {b && (
        <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
          {(b.readyForExam || b.rating != null) && (
            <div className="flex items-center gap-2">
              {b.readyForExam && <Badge tone="ok">Prêt examen</Badge>}
              {b.rating != null && (
                <span className="flex items-center gap-0.5 text-xs font-semibold text-amber-600">
                  <Star size={13} className="fill-amber-400 text-amber-400" aria-hidden />
                  {b.rating}/5
                </span>
              )}
            </div>
          )}
          <p className="whitespace-pre-line text-sm text-body">{b.summary}</p>
          {b.nextObjective && (
            <p className="text-sm text-primary-700">
              <strong>Prochain cours :</strong> {b.nextObjective}
            </p>
          )}
          {b.examReason && <p className="text-sm text-secondary">Avis examen : {b.examReason}</p>}
          <PrivateNote text={b.privateNote} />
          {b.competencies && b.competencies.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {b.competencies.map((c, i) => (
                <Badge key={`${c.label}-${i}`}>
                  {c.label} · {LEVEL_LABEL[c.level] ?? c.level}
                </Badge>
              ))}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

export default function LessonsSheet() {
  const target = useUiStore((s) => s.lessonsStudent);
  const close = useUiStore((s) => s.closeLessons);
  const openBilan = useUiStore((s) => s.openBilan);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['student-lessons', target?.id],
    queryFn: () => instructorApi.getStudentLessons(target!.id),
    enabled: !!target,
  });

  if (!target) return null;

  return (
    <Sheet title="Historique des leçons" description={target.name} onClose={close} className="sm:max-w-2xl">
      {isLoading && <p className="text-sm text-secondary">Chargement…</p>}
      {isError && <p className="rounded-xl bg-danger-light px-3 py-2 text-sm text-danger-dark">Impossible de charger les leçons.</p>}
      {data && data.length === 0 && (
        <p className="rounded-row bg-surface-light px-4 py-6 text-center text-sm text-secondary">Aucune leçon passée pour cet élève.</p>
      )}
      <ul className="space-y-3">
        {data?.map((lesson) => (
          <LessonRow
            key={lesson.id}
            lesson={lesson}
            onBilan={() => {
              close();
              openBilan({ candidateId: target.id, candidateName: target.name, lessonId: lesson.id });
            }}
          />
        ))}
      </ul>
    </Sheet>
  );
}
